/**
 * VersandAnhaenge - Teil 4: the Eingang's files and how each one is sent.
 *
 * Konzept 8.2/4.5: ALL files of the Eingang (not the selected task's subset), each
 * active by default, channel selectable per file, unneeded ones switched off.
 * Deactivating greys the row instead of hiding it - the choice is information. Two
 * different things grey a row, so they are labelled apart: a broker switched the file
 * off, or the system seeded it off (an inline signature picture). Only the first is
 * warned about at the Gesamtfreigabe (`deactivated_by IS NOT NULL`, E3), so a row that
 * says "vom System" is also saying "this will not stop the release".
 *
 * Channels: CHANNEL_CATALOG filtered to deliverable + adapter present. PEAX/E-Post are
 * inactive, and a channel that cannot deliver has no business in the dropdown.
 *
 * State is the server's: one task_attachment row per file (F1), read and PATCHed through
 * GET/PATCH /tasks/:id/attachments. The default channel comes from the Kanalsteuerung.
 *
 * [Source: docs/eingangsverarbeitung-epic.md §3 WS-C C4]
 */
import { Paperclip } from 'lucide-react';
import {
  CHANNEL_CATALOG,
  DEFAULT_CHANNEL_CONFIGS,
  type ChannelKey,
  type TaskAttachmentPatch,
  type TaskAttachmentView,
} from '@brokly/shared';
import { cn } from '../../../lib/cn';
import { fileSize } from '../../../lib/format';
import { isInlinePreviewable } from '../../documents/components/InlineDocumentViewer';

/** Zustellungsfähig AND actually deliverable today (an adapter exists). */
const VERSAND_CHANNELS = CHANNEL_CATALOG.filter(
  (c) => c.zustellung && DEFAULT_CHANNEL_CONFIGS[c.key].active,
);

interface VersandAnhaengeProps {
  /** The task's Versandanhaenge, as the server holds them. */
  rows: TaskAttachmentView[];
  onPatch: (documentId: string, patch: TaskAttachmentPatch) => void;
  /** The attachment currently previewed in Teil 1, so the row can show it as open. */
  previewId: string | null;
  onOpenPreview: (id: string) => void;
}

export function VersandAnhaenge({ rows, onPatch, previewId, onOpenPreview }: VersandAnhaengeProps) {
  if (rows.length === 0) {
    return <p className="text-sm text-muted-foreground">Keine Anhänge.</p>;
  }

  const aktiv = rows.filter((r) => r.is_active).length;

  return (
    <div className="flex flex-col gap-2">
      <ul className="flex flex-col gap-1">
        {rows.map((attachment) => {
          const active = attachment.is_active;
          const channel = attachment.delivery_channel;
          const previewable = isInlinePreviewable(attachment.mime_type);
          return (
            <li
              key={attachment.document_id}
              className={cn(
                'flex items-center gap-2 rounded-md border border-border px-2.5 py-1.5',
                // «deaktivierte Dateien greyed» (C4) — still listed, visibly not sent.
                !active && 'opacity-55',
              )}
            >
              <input
                type="checkbox"
                checked={active}
                onChange={(e) => onPatch(attachment.document_id, { is_active: e.target.checked })}
                aria-label={`${attachment.filename} versenden`}
                className="size-4 shrink-0 accent-[hsl(var(--primary))]"
              />

              {/* The filename doubles as the Teil-1 preview trigger — same affordance as
                  the Eingang's own file list, so one file behaves the same in both. */}
              <button
                type="button"
                disabled={!previewable}
                onClick={() => onOpenPreview(attachment.document_id)}
                aria-pressed={previewable ? attachment.document_id === previewId : undefined}
                title={
                  previewable ? 'Vorschau öffnen' : 'Für diesen Dateityp gibt es keine Vorschau'
                }
                className="flex min-w-0 flex-1 items-center gap-2 text-left text-sm enabled:hover:underline disabled:cursor-default disabled:text-muted-foreground"
              >
                <Paperclip className="size-3.5 shrink-0 text-muted-foreground" />
                <span className="min-w-0 truncate">{attachment.filename}</span>
              </button>

              {/* Which of the two deactivations this is. Nothing on an active row: the
                  question only arises once a file is greyed. */}
              {!active && (
                <span
                  className="shrink-0 rounded-md bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground"
                  title={
                    attachment.deactivated_by_user
                      ? 'Von einer Person deaktiviert - wird bei der Gesamtfreigabe gemeldet'
                      : 'Automatisch deaktiviert, z. B. ein Bild aus der Signatur'
                  }
                >
                  {attachment.deactivated_by_user ? 'deaktiviert' : 'vom System'}
                </span>
              )}

              <span className="shrink-0 text-xs text-muted-foreground">
                {fileSize(attachment.size_bytes)}
              </span>

              {/* Versandkanal pro Datei, übersteuerbar (8.2). Disabled on a deactivated
                  file: a channel for something that is not being sent means nothing. */}
              <select
                value={channel}
                disabled={!active}
                onChange={(e) =>
                  onPatch(attachment.document_id, { delivery_channel: e.target.value as ChannelKey })
                }
                aria-label={`Versandkanal für ${attachment.filename}`}
                className="h-7 shrink-0 rounded-md border border-border bg-background px-1.5 text-xs disabled:cursor-not-allowed disabled:text-muted-foreground"
              >
                {VERSAND_CHANNELS.map((c) => (
                  <option key={c.key} value={c.key}>
                    {c.label}
                  </option>
                ))}
              </select>
            </li>
          );
        })}
      </ul>

      <p className="text-xs text-muted-foreground">
        {aktiv} von {rows.length} Dateien werden versendet
      </p>
    </div>
  );
}
